import { Head, Link, usePage } from '@inertiajs/react'
import { useState } from 'react'
import AdminLayout from '../../components/layout/AdminLayout'

export default function QrCodesShow() {
  const { qr_code, recent_scans } = usePage().props
  const [copied, setCopied] = useState(false)

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(qr_code.scan_url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <AdminLayout>
      <Head title={`QR Code - ${qr_code.campaign_name || qr_code.tracking_id}`} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link href="/qr_codes" className="text-sm text-gray-600 hover:text-gray-900 mb-4 inline-flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour aux QR Codes
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{qr_code.campaign_name || 'QR Code'}</h1>
              {qr_code.property_address && (
                <p className="mt-1 text-sm text-gray-600">{qr_code.property_address}</p>
              )}
            </div>
            <div className="flex items-center gap-4">
              {qr_code.active ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  Actif
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  Inactif
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Colonne principale */}
          <div className="lg:col-span-2 space-y-8">
            {/* QR Code */}
            <div className="bg-white shadow-sm rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Votre QR Code</h2>
              <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg">
                <div
                  className="bg-white p-4 rounded-lg shadow-sm"
                  dangerouslySetInnerHTML={{ __html: qr_code.qr_svg }}
                />
                <p className="mt-4 text-sm text-gray-600 text-center">
                  Tracking ID: <span className="font-mono font-medium">{qr_code.tracking_id}</span>
                </p>
              </div>

              {/* Boutons de téléchargement */}
              <div className="mt-6 grid grid-cols-3 gap-4">
                <a
                  href={`/qr_codes/${qr_code.id}/download?format=svg`}
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  SVG
                </a>
                <a
                  href={`/qr_codes/${qr_code.id}/download?format=png`}
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  PNG
                </a>
                <a
                  href={`/qr_codes/${qr_code.id}/download?format=pdf`}
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  PDF
                </a>
              </div>
            </div>

            {/* Scans récents */}
            {recent_scans && recent_scans.length > 0 && (
              <div className="bg-white shadow-sm rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Scans récents</h2>
                <div className="space-y-3">
                  {recent_scans.map((scan) => (
                    <div key={scan.id} className="flex items-center justify-between py-2 border-b last:border-b-0">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                          {scan.device_type === 'mobile' ? '📱' : '💻'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {scan.device_type === 'mobile' ? 'Mobile' : 'Desktop'}
                          </p>
                          <p className="text-xs text-gray-500">{scan.ip_address}</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500">
                        {new Date(scan.scanned_at).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Statistiques */}
          <div className="space-y-6">
            <div className="bg-white shadow-sm rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Statistiques</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Total scans</p>
                  <p className="text-2xl font-bold text-gray-900">{qr_code.total_scans || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Scans aujourd'hui</p>
                  <p className="text-2xl font-bold text-blue-600">{qr_code.scans_today || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Scans cette semaine</p>
                  <p className="text-2xl font-bold text-green-600">{qr_code.scans_this_week || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Leads générés</p>
                  <p className="text-2xl font-bold text-purple-600">{qr_code.leads_generated || 0}</p>
                </div>
              </div>
            </div>

            {/* URL de scan */}
            <div className="bg-white shadow-sm rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">URL de scan</h2>
              <div className="bg-gray-50 rounded p-3">
                <p className="text-xs font-mono text-gray-600 break-all">{qr_code.scan_url}</p>
              </div>
              <button
                onClick={handleCopyUrl}
                className={`mt-3 w-full inline-flex items-center justify-center px-4 py-2 border text-sm font-medium rounded-md transition-colors ${
                  copied
                    ? 'border-green-500 text-green-700 bg-green-50'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                {copied ? (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Copié
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copier l'URL
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
